/**
 * The language bar shared by every mode.
 *
 * Source on the left, target on the right, swap in the middle. The swap is
 * disabled when the source is `auto` because "swap" has no meaning when the
 * source has not been chosen yet — offering it would silently invent a
 * language the user never picked.
 */

import type { LanguageCode } from "@shared/types/language";
import { languageLabel, resolveLanguage } from "@shared/languages";
import { LanguagePicker, LanguageChip } from "./LanguagePicker";
import { SwapButton } from "./ui";
import { IconDetect, IconSparkle } from "./icons";

interface Props {
  source: LanguageCode | "auto";
  target: LanguageCode;
  onSourceChange: (value: LanguageCode | "auto") => void;
  onTargetChange: (value: LanguageCode) => void;
  onSwap: () => void;
  disabled?: boolean;
  /** Shown under the target when auto-detection is resolving a language. */
  detectedLabel?: string | null;
  /** Stacked puts From on top and To below (text mode); row is side by side. */
  layout?: "row" | "stacked";
}

export function LanguageBar({ source, target, onSourceChange, onTargetChange, onSwap, disabled = false, detectedLabel = null, layout = "row" }: Props) {
  const sourceLanguage = resolveLanguage(source === "auto" ? "en" : source);
  const targetLanguage = resolveLanguage(target);

  return (
    <div className={`langbar${layout === "stacked" ? " langbar--stacked" : ""}`}>
      <div className="langbar__slot">
        <LanguagePicker value={source} onChange={onSourceChange} label="From" includeAuto disabled={disabled} />
        <div className="langbar__meta">
          {source === "auto" ? (
            <>
              <IconDetect size={12} />
              {detectedLabel ? <span style={{ color: "var(--accent)" }}>{detectedLabel}</span> : <span>Detecting automatically</span>}
            </>
          ) : (
            <span>{sourceLanguage?.name ?? source}</span>
          )}
        </div>
      </div>

      <SwapButton onClick={onSwap} disabled={disabled || source === "auto"} />

      <div className="langbar__slot" style={{ alignItems: "flex-end" }}>
        <div style={{ width: "100%" }}>
          <LanguagePicker value={target} onChange={(value) => onTargetChange(value as LanguageCode)} label="To" disabled={disabled} />
        </div>
        <div className="langbar__meta" style={{ justifyContent: "flex-end" }}>
          <IconSparkle size={12} style={{ color: "var(--accent)" }} />
          <span>{targetLanguage?.name ?? languageLabel(target)}</span>
        </div>
      </div>
    </div>
  );
}

export { LanguageChip };
